// 分析画面で使用する固定順ステップ定義

export interface StepDefinition {
  /** UI 上のキー（フロント全体の表示単位） */
  key: string;
  /** 表示ラベル */
  label: string;
  /** API側のステップキー（複数指定すると当該UIキーで合算する） */
  apiKeys: string[];
}

/**
 * UI 上のステップ定義（固定順、8グループ）
 * 「書類選考〜4次選考」は API 側では 5 ステップだが、UI では「選考」として 1 つに集約する。
 */
export const ANALYSIS_STEPS: StepDefinition[] = [
  { key: 'registration', label: '登録', apiKeys: ['registration'] },
  { key: 'ca_meeting', label: 'CA面談', apiKeys: ['ca_meeting'] },
  { key: 'suggest', label: '求人提案', apiKeys: ['suggest'] },
  {
    key: 'screening',
    label: '選考',
    apiKeys: [
      'document_screening',
      '1st_interview',
      '2nd_interview',
      '3rd_interview',
      '4th_interview',
    ],
  },
  { key: 'final_meeting', label: '最終選考', apiKeys: ['final_meeting'] },
  { key: 'offer_meeting', label: 'オファー面談', apiKeys: ['offer_meeting'] },
  { key: 'offered', label: '内定', apiKeys: ['offered'] },
  { key: 'closed', label: '決定', apiKeys: ['closed'] },
];

/**
 * テーブル表示用の API ステップ詳細（12項目）
 * サマリーは UI 集約（screening 等）で表示する一方、テーブルは API レベルで
 * 「書類選考 / 1次選考 / 2次選考 / 3次選考 / 4次選考」を別列として展開する。
 * uiKey で visible_steps（UI 集約キー）と紐付ける。
 */
export interface TableStepDefinition {
  /** API のステップキー */
  key: string;
  /** 表示ラベル */
  label: string;
  /** 対応する UI 集約キー（visible_steps と紐付け） */
  uiKey: string;
}

export const TABLE_STEPS: TableStepDefinition[] = [
  { key: 'registration', label: '登録', uiKey: 'registration' },
  { key: 'ca_meeting', label: 'CA面談', uiKey: 'ca_meeting' },
  { key: 'suggest', label: '求人提案', uiKey: 'suggest' },
  { key: 'document_screening', label: '書類選考', uiKey: 'screening' },
  { key: '1st_interview', label: '1次選考', uiKey: 'screening' },
  { key: '2nd_interview', label: '2次選考', uiKey: 'screening' },
  { key: '3rd_interview', label: '3次選考', uiKey: 'screening' },
  { key: '4th_interview', label: '4次選考', uiKey: 'screening' },
  { key: 'final_meeting', label: '最終選考', uiKey: 'final_meeting' },
  { key: 'offer_meeting', label: 'オファー面談', uiKey: 'offer_meeting' },
  { key: 'offered', label: '内定', uiKey: 'offered' },
  { key: 'closed', label: '決定', uiKey: 'closed' },
];

// グループ化オプション定義
export const GROUP_BY_OPTIONS = [
  { value: 'candidate_user', label: '求職者担当者' },
  { value: 'company_user', label: '企業担当者' },
  { value: 'job_user', label: '求人担当者' },
  { value: 'company', label: '企業' },
] as const;

export type GroupByValue = (typeof GROUP_BY_OPTIONS)[number]['value'];

// 粒度オプション定義
export const GRANULARITY_OPTIONS = [
  { value: 'day', label: '日別' },
  { value: 'week', label: '週別' },
  { value: 'month', label: '月別' },
] as const;

export type GranularityValue = (typeof GRANULARITY_OPTIONS)[number]['value'];

// メトリクスオプション定義
export const METRIC_OPTIONS = [
  { value: 'uu', label: '人数' },
  { value: 'count', label: '件数' },
] as const;

// カウントモードオプション定義
export const COUNT_MODE_OPTIONS = [
  { value: 'total', label: '総数カウント' },
  { value: 'registered_cohort', label: '登録カウント' },
] as const;

export type CountModeValue = (typeof COUNT_MODE_OPTIONS)[number]['value'];

/**
 * GroupBy 設定時のスタックバーチャートの色パレット（Figma primitive 準拠）
 * series 配列の順に index で割り当て、10 色を超える場合は循環。
 * 外部からは colorForSeries() 経由で参照する。
 */
const GROUP_CHART_COLORS = [
  '#0e295d', // blue/950
  '#0849c5', // blue/800
  '#0674ff', // blue/600
  '#48b7ff', // blue/400
  '#b5e2ff', // blue/200
  '#89500a', // yellow/800
  '#d19100', // yellow/600
  '#ffd70d', // yellow/400
  '#454545', // gray/800
  '#5d5d5d', // gray/600
  '#888888', // gray/400
];

/** 担当者・企業が紐づかないイベントを集約する仮想 series.key（バックエンドと一致させること） */
export const OTHERS_GROUP_KEY = 'others';
/** OTHERS_GROUP_KEY 用の固定色（中性グレー / Semantic/Text/Secondary 相当） */
const OTHERS_GROUP_COLOR = '#88898b';

/**
 * series.key と index に対する塗色を返す。
 * - OTHERS_GROUP_KEY は固定でグレー（パレット循環の対象外）
 * - それ以外は GROUP_CHART_COLORS を循環
 */
export function colorForSeries(seriesKey: string, index: number): string {
  if (seriesKey === OTHERS_GROUP_KEY) return OTHERS_GROUP_COLOR;
  return GROUP_CHART_COLORS[index % GROUP_CHART_COLORS.length];
}
